import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { OtherTranslationsGridProps } from './types';
import { TranslationReviewPanel } from './TranslationReviewPanel';

export const OtherTranslationsGrid: React.FC<OtherTranslationsGridProps> = ({
  translations,
  productId,
  onCopyText,
  onViewHistory,
  onEditTranslation,
  onNavigateToTranslate,
}) => {
  const { t } = useTranslation();
  const [expandedLanguage, setExpandedLanguage] = useState<string | null>(null);

  return (
    <Box mb={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={1.5}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('productDetails.otherLanguagesTitle', { count: translations.length })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('productDetails.otherLanguagesDescription')}
          </Typography>
        </Box>
        {translations.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onNavigateToTranslate}
          >
            {t('products.add')}
          </Button>
        )}
      </Stack>
      
      {translations.length === 0 ? (
        <Paper 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <LanguageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('productDetails.noTranslationsYet')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t('productDetails.addFirstTranslation')}
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={onNavigateToTranslate}
          >
            {t('products.add')}
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {translations.map((translation) => (
            <TranslationReviewPanel
              key={translation.language}
              translation={translation}
              productId={productId}
              collapsible
              expanded={expandedLanguage === translation.language}
              onToggleExpanded={() => {
                setExpandedLanguage((current) => current === translation.language ? null : translation.language);
              }}
              onCopyText={onCopyText}
              onViewHistory={onViewHistory}
              onEditTranslation={onEditTranslation}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
